import React, { useState, useRef, useEffect, useContext, createContext, useCallback } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import { findUserRole } from "../../shared/utils/general";
import { useAuth } from "./AuthContext";
import { useMyVwContext } from "../../shared/hooks/use-my-vw-context";
import { UserData, UserAccountPrivileges, SecurityQuestion, UserSecurityQuestion, UserAccountDetails, ManageContactsPayload } from "../../shared/typings/main";
import { useFeatureServices } from "../../shared/hooks/use-feature-services";

interface UserContextType {
  registrationSufficiencyCheck: String[];
  securityQuestions: SecurityQuestion[];
  TOS: any | null;
  user?: UserData;
  userAccountDetails: any;
  userAccountPrivileges: UserAccountPrivileges[];
  userSecurityQuestions: UserSecurityQuestion[];

  fetchUserSecurityQuestions: () => Promise<void>;
  refetchUserProfile: () => Promise<UserData>;
  setUserProfile: (profileData: UserData) => void;
  updateUserProfile: (profileData: Partial<UserData>) => void;
  userRegistrationSufficiencyCheck: () => Promise<any>;
  fetchSaveAdditionalContacts: (payload: ManageContactsPayload) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [registrationSufficiencyCheck, setRegistrationSufficiencyCheck] = useState<String[]>([]);
  const [securityQuestions, setSecurityQuestions] = useState<SecurityQuestion[]>([]);
  const [TOS, setTOS] = useState<any | null>(null);
  const [user, setUser] = useState<UserData | undefined>();
  const [userAccountDetails, setUserAccountDetails] = useState<UserAccountDetails>();
  const [userAccountPrivileges, setUserAccountPrivileges] = useState<UserAccountPrivileges[]>([]);
  const [userSecurityQuestions, setUserSecurityQuestions] = useState<UserSecurityQuestion[]>([]);

  const registrationCheckDone = useRef<boolean>(false);
  const userDataFetched = useRef<boolean>(false);

  const { authService } = useMyVwContext();
  const { myvwFaRolloutRegistration } = useFlags();
  const { isAuthenticated } = useAuth();
  const { 'locale-service': localeService } = useFeatureServices();
  const localeCode = `${localeService.language}-${localeService.countryCode}`;

  const fetchSecurityQuestions = async () => {
    try {
      const { getApprovedSecurityQuestions } =
        await authService.getApprovedSecurityQuestionsGqlRequest(localeCode);
      setSecurityQuestions(getApprovedSecurityQuestions);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUserSecurityQuestions = async () => {
    try {
      const { getUserSpecificSecurityQuestions } =
        await authService.getSecurityQuestionGqlRequest(localeCode);
      setUserSecurityQuestions(getUserSpecificSecurityQuestions);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUserAccountPrivileges = async () => {
    try {
      const { getUserAccountPrivileges } =
        await authService.getUserAccountPrivilegesGqlRequest(localeCode);

      const userId =
        getUserAccountPrivileges.length > 0 && getUserAccountPrivileges[0].userId
          ? getUserAccountPrivileges[0].userId
          : '';

      const userRole = findUserRole(getUserAccountPrivileges);

      setUserAccountPrivileges(getUserAccountPrivileges);

      updateUserProfile({
        id: userId,
        userRole: userRole,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUserAccountDetails = async () => {
    try {
      const { getUserAccountDetails } =
        await authService.getUserAccountDetailsGqlRequest(localeCode);
      setUserAccountDetails(getUserAccountDetails);
    } catch (error) {
      console.error('There was an error getting the user account details', error);
    }
  };

   const fetchSaveAdditionalContacts = async (payload: ManageContactsPayload) => {
    try {
      await authService.updateProfileContactsGqlRequest(payload);

      await refetchUserProfile();
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  const userRegistrationSufficiencyCheck = async () => {
    try {
      let data: any;
      if (!myvwFaRolloutRegistration) {
        data = await authService.userRegistrationSufficiencyCheck(localeCode);
      } else {
        data = await authService.userRegistrationSufficiencyCheckWithoutVehicleValidations(localeCode);
      }

      setRegistrationSufficiencyCheck(data);
      return data;
    } catch (error) {
      setRegistrationSufficiencyCheck([]);
      console.error('There was an error while checking the user registration', error);
      authService.apiLog({
        code: 'There was an error while checking the user registration',
        message: error,
      });
      return [];
    }
  };

  const getTOSFromSessionStorage = (): void => {
    const tosData = sessionStorage.getItem('enhancedMyVwTos');
    setTOS(tosData);
  };

  const refetchUserProfile = useCallback(async () => {
    try {
      const { getUserProfileByUserId } =
        await authService.getUserProfileGqlRequest(localeCode);

      setUser(getUserProfileByUserId);

      await Promise.all([
        fetchSecurityQuestions(),
        fetchUserSecurityQuestions(),
        fetchUserAccountPrivileges(),
        fetchUserAccountDetails()
      ]);

      return getUserProfileByUserId;
    } catch (error) {
      console.error('Error refetching user profile:', error);
      throw error;
    }
  }, [authService]);

  // Fetch user data when authenticated
  useEffect(() => {
    const fetchUserData = async () => {
      if (isAuthenticated && !userDataFetched.current) {
        try {
          userDataFetched.current = true;

          const { getUserProfileByUserId } =
            await authService.getUserProfileGqlRequest(localeCode);

          setUser(getUserProfileByUserId);

          await Promise.all([
            fetchSecurityQuestions(),
            fetchUserSecurityQuestions(),
            fetchUserAccountPrivileges(),
            fetchUserAccountDetails()
          ]);

        } catch (error) {
          console.error(error);
          userDataFetched.current = false;
        }
      }
    };

    fetchUserData();
  }, [isAuthenticated, authService]);

  // Registration check
  useEffect(() => {
    if (isAuthenticated && !registrationCheckDone.current) {
      registrationCheckDone.current = true;
      userRegistrationSufficiencyCheck();
      getTOSFromSessionStorage();
    } else if (!isAuthenticated) {
      registrationCheckDone.current = false;
      setUser(undefined);
      userDataFetched.current = false;
    }
  }, [isAuthenticated]);

  const updateUserProfile = useCallback((profileData: Partial<UserData>) => {
    setUser((prev) => ({ ...prev, ...profileData }) as UserData);
  }, []);

  const setUserProfile = useCallback((profileData: UserData) => {
    setUser(profileData);
  }, []);

  return (
    <UserContext.Provider
      value={{
        fetchUserSecurityQuestions,
        refetchUserProfile,
        registrationSufficiencyCheck,
        securityQuestions,
        setUserProfile,
        TOS,
        updateUserProfile,
        user,
        userAccountDetails,
        userAccountPrivileges,
        userRegistrationSufficiencyCheck,
        userSecurityQuestions,
        fetchSaveAdditionalContacts
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) throw new Error('useUser must be used within UserProvider');

  return context;
};